import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface NavMenuItem {
  label: string;
  to: string;
  /** A small amber tag after the label ("NEW"). */
  badge?: string;
}

// A header dropdown (Offers, Resources).
//
// v3's own audit failed this outright: its menus opened on pointer hover only, so a
// keyboard user could not reach Categories, Popular or Resources from the header at
// all. Here hover still opens it — the pointer behaviour is unchanged — but focus and
// Enter/Space open it too, Escape closes and returns focus to the trigger, and arrow
// keys walk the items. The menu is a real listbox of links, so middle-click and
// open-in-new-tab work as they would anywhere else.
// `caret={false}`: the design draws the caret on Resources only; Booking reads as a
// plain nav word (DC:127 vs DC:147).
// `adornment` sits after the label (Offers' % circle); `active` marks the trigger as the
// current section (the accent-tint pill the plain nav links use).
// `to` (client decision, 30 Oct 2026 — Offers and Booking): the trigger is a link to its
// main page, so a click goes there; the menu opens on hover, and from the keyboard with
// ArrowDown on the focused link.
export function NavMenu({
  label,
  groups,
  caret = true,
  adornment,
  active = false,
  to,
}: {
  label: string;
  to?: string;
  groups: Array<{ title?: string; items: NavMenuItem[] }>;
  caret?: boolean;
  adornment?: ReactNode;
  active?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);
  // Whether the CLICK opened it, as opposed to hover. Without this the trigger is a
  // plain toggle, and a desktop mouse user gets: hover opens the menu, then the very
  // click that was meant to open it closes it again. Tracking the source means a
  // click on a hover-opened menu keeps it open, and only a second click closes.
  const openedByClick = useRef(false);

  // Closing on outside pointer-down as well as on blur: a click elsewhere should
  // dismiss the menu even when it lands on something non-focusable.
  useEffect(() => {
    if (!open) return;
    function onDocDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      openedByClick.current = false;
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      // Defer so the panel exists before we look for something to focus.
      requestAnimationFrame(() => wrapRef.current?.querySelector<HTMLAnchorElement>('a')?.focus());
    }
  }

  const triggerClass = active ? 'navmenu__trigger navmenu__trigger--active' : 'navmenu__trigger';
  const triggerBody = (
    <>
      {label}
      {adornment}
      {caret && (
        <span className="navmenu__caret" aria-hidden="true">
          ▾
        </span>
      )}
    </>
  );

  return (
    <div
      ref={wrapRef}
      className="navmenu"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        openedByClick.current = false;
        setOpen(false);
      }}
      onKeyDown={onKeyDown}
    >
      {to ? (
        <Link
          ref={triggerRef as React.RefObject<HTMLAnchorElement>}
          to={to}
          className={triggerClass}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen(false)}
        >
          {triggerBody}
        </Link>
      ) : (
        <button
          ref={triggerRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className={triggerClass}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => {
            if (open && openedByClick.current) {
              openedByClick.current = false;
              setOpen(false);
              return;
            }
            openedByClick.current = true;
            setOpen(true);
          }}
        >
          {triggerBody}
        </button>
      )}

      {open && (
        <div className="navmenu__panel">
          {groups.map((g, i) => (
            <div className="navmenu__group" key={g.title ?? i}>
              {g.title && <div className="navmenu__grouptitle">{g.title}</div>}
              {g.items.map((item) => (
                <Link key={item.to + item.label} className="navmenu__item" to={item.to} onClick={() => setOpen(false)}>
                  {item.label}
                  {item.badge ? (
                    <span className="navmenu__new" aria-hidden="true">
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
